import { Link, Navigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDate, titleCase } from '../../lib/format'
import type { KycDoc, Organization, UserProfile } from '../../types'
import { Alert, Card, CardBody, CardHeader, EmptyState, Skeleton, StatusBadge } from '../../components/ui'
import { DocIcon, GradCapIcon } from '../../components/icons'

interface CourseProgressRow {
  course_id?: string
  course_name?: string
  progress_pct?: number
  is_completed?: boolean
}

interface GovUserDetailResponse {
  ok: boolean
  profile: UserProfile
  kyc_docs: KycDoc[]
  course_progress: CourseProgressRow[]
  organization: Organization | null
}

export default function UserDetail() {
  const { uid } = useParams<{ uid: string }>()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['gov', 'user', uid],
    queryFn: () => api.get<GovUserDetailResponse>(`/gov/users/${uid}`),
    enabled: Boolean(uid),
  })

  if (!uid) return <Navigate to="/users" replace />
  if (isError) {
    return (
      <Alert tone="danger">
        {(error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/users">Back to users</Link></div>
      </Alert>
    )
  }
  if (isLoading || !data) return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>

  const { profile, kyc_docs, course_progress, organization } = data

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{profile.full_name || profile.email}</h1>
          <p className="page-head__sub">{profile.email}</p>
        </div>
        <StatusBadge status={profile.status} />
      </div>

      <div className="grid grid--2" style={{ marginBottom: 'var(--sp-6)' }}>
        <Card>
          <CardHeader title="Profile" />
          <CardBody>
            <div className="stack">
              <Row label="Role" value={titleCase(profile.role) || '—'} />
              <Row label="Phone" value={profile.phone || '—'} />
              <Row label="Location" value={[titleCase(profile.district_id), titleCase(profile.region_id)].filter(Boolean).join(', ') || '—'} />
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Organisation" />
          <CardBody>
            {organization ? (
              <div className="stack">
                <Row label="Name" value={organization.name || '—'} />
                <Row label="Type" value={titleCase(organization.type) || '—'} />
              </div>
            ) : (
              <p className="page-head__sub" style={{ margin: 0 }}>Not linked to an organisation.</p>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="grid grid--2">
        <Card>
          <CardHeader title="KYC documents" />
          <CardBody flush>
            {kyc_docs.length === 0 ? (
              <div style={{ padding: 'var(--sp-6)' }}>
                <EmptyState icon={<DocIcon size={22} />} title="No documents" body="" />
              </div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Type</th><th>Uploaded</th><th>Status</th></tr></thead>
                  <tbody>
                    {kyc_docs.map((d, i) => (
                      <tr key={d.id ?? i}>
                        <td>{d.doc_type || '—'}</td>
                        <td>{formatDate(d.uploaded_at)}</td>
                        <td><StatusBadge status={d.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Training progress" />
          <CardBody flush>
            {course_progress.length === 0 ? (
              <div style={{ padding: 'var(--sp-6)' }}>
                <EmptyState icon={<GradCapIcon size={22} />} title="No courses started" body="" />
              </div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Course</th><th className="table__num">Progress</th></tr></thead>
                  <tbody>
                    {course_progress.map((c, i) => (
                      <tr key={c.course_id ?? i}>
                        <td>{c.course_name}</td>
                        <td className="table__num">
                          {c.is_completed ? <StatusBadge status="completed" /> : `${c.progress_pct ?? 0}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 550 }}>{value}</span>
    </div>
  )
}
